import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { day, get, money, post } from '../lib/api';
import { Badge, Button, Card, ErrorNote, Field, Input, Loading, PageHeader, Select } from '../ui';

type Row = {
  id: string; customer_id: string; name: string; currency: string; business_kind: string;
  imports: number; last_import_at: string | null;
  last_audit: { id: string; ran_at: string; wasted: number; total_cost: number; summary: string } | null;
};

export default function Accounts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const list = useQuery({ queryKey: ['accounts'], queryFn: () => get<{ data: Row[] }>('/v1/accounts') });

  return (
    <>
      <PageHeader
        title="Accounts"
        subtitle="One row per Google Ads account you have been given read access to."
        actions={<Button variant="primary" onClick={() => setOpen((o) => !o)}><Plus className="size-4" />Add an account</Button>}
      />

      {open && <NewAccount onDone={() => { setOpen(false); qc.invalidateQueries({ queryKey: ['accounts'] }); }} />}

      {list.isLoading ? <Loading /> : list.error ? <ErrorNote error={list.error} /> : !list.data?.data.length ? (
        <Card>
          <h2 className="text-sm font-bold">Nothing here yet</h2>
          <p className="mt-1 max-w-prose text-sm text-t2">
            Add the prospect&rsquo;s customer ID, then drop in the reports you downloaded from their account.
            The whole point is to have the findings ready <em>before</em> the meeting, not during it.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.data.data.map((a) => (
            <Link key={a.id} to={`/accounts/${a.id}`} className="block">
              <Card className="transition-colors hover:border-bd2">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold">{a.name}</span>
                      <span className="num text-xs text-t3">{formatCustomerId(a.customer_id)}</span>
                      {a.business_kind !== 'unknown' && <Badge>{a.business_kind}</Badge>}
                    </div>
                    <p className="mt-1 max-w-prose text-sm text-t2">
                      {a.last_audit
                        ? a.last_audit.summary
                        : a.imports
                          ? `${a.imports} report${a.imports === 1 ? '' : 's'} imported. No audit run yet.`
                          : 'Nothing imported yet.'}
                    </p>
                  </div>
                  {a.last_audit && Number(a.last_audit.wasted) > 0 && (
                    <div className="text-right">
                      <div className="num text-xl font-bold text-err">{money(a.last_audit.wasted, a.currency)}</div>
                      <div className="text-xs text-t3">at risk · {day(a.last_audit.ran_at)}</div>
                    </div>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

/** 1234567890 → 123-456-7890, the way Google prints it. */
export function formatCustomerId(id: string) {
  return id.length === 10 ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}` : id;
}

function NewAccount({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({ customer_id: '', name: '', business_kind: 'calls', currency: 'INR' });
  const add = useMutation({
    mutationFn: () => post('/v1/accounts', form),
    onSuccess: onDone,
  });

  return (
    <Card className="mb-4">
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => { e.preventDefault(); add.mutate(); }}
      >
        <Field label="Business name">
          <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Customer ID" hint="Top right of their Google Ads screen, like 123-456-7890.">
          <Input value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} />
        </Field>
        <Field label="What does this business run on?" hint="Several rules mean different things depending on this.">
          <Select className="w-full" value={form.business_kind} onChange={(e) => setForm({ ...form, business_kind: e.target.value })}>
            <option value="calls">Phone calls</option>
            <option value="forms">Enquiry forms</option>
            <option value="ecommerce">Online sales</option>
            <option value="unknown">Not sure yet</option>
          </Select>
        </Field>
        <Field label="Currency">
          <Select className="w-full" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            <option value="INR">INR ₹</option>
            <option value="USD">USD $</option>
            <option value="AED">AED</option>
            <option value="GBP">GBP £</option>
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <ErrorNote error={add.error} />
          <Button type="submit" variant="primary" loading={add.isPending}
            disabled={!form.name.trim() || !form.customer_id.trim()} className="mt-2">
            Add it
          </Button>
        </div>
      </form>
    </Card>
  );
}
